<template>
  <div class="voip">
    <AudioOutputs ref="AudioOutputs" />

    <div class="voip__hospital-name">{{ hospitalName }}</div>
    <div class="voip__department-name">{{ departmentName }}</div>
    <div class="d-flex justify-content-between">
      <div class="d-flex">
        <div>
          <button v-if="!muted" class="voip-control__button voip-control__microphone-enabled" @click="toggleMicrophone">
            <v-icon name="microphone" color="black" scale="1.5" />
          </button>
          <button v-if="muted" class="voip-control__button voip-control__microphone-disabled" @click="toggleMicrophone">
            <v-icon name="microphone_off" color="black" scale="1.5" />
          </button>
        </div>
        <div style="padding-left: 2rem">
          <button v-if="soundIsOn" class="voip-control__button voip-control__sound-enabled" @click="chooseSoundOutput">
            <v-icon name="sound" color="black" scale="1.5" />
          </button>
        </div>
      </div>
      <div class="align-items-center d-flex">
        <div class="voip-control__call-state">
          <div v-if="showConnecting" class="voip-control__call-state__block">Connecting...</div>
          <div v-if="showRecording" class="voip-control__call-state__block voip-controls__recording">REC</div>
          <div v-if="showRecording" class="voip-control__call-state__block voip-control__recording__pulsate-circle" />
          <div v-if="showDisconnected" class="voip-control__call-state__block disconnected">Disconnected</div>
          <div v-if="showCallTime" class="voip-control__call-state__block voip-controls__call-time">{{ callTime }}</div>
        </div>
        <button class="voip-control__button voip-controls__hangup_button" @click="endCall">
          <v-icon name="hang_up" color="#ffffff" scale="1.5" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { CallStatus } from "../CallStatus";
import { minutesAndSecondsElapsed } from "@/Utils/timeFormattingUtils";
import { mapActions, mapState } from "vuex";
import Icon from "vue-awesome/components/Icon";
import { showOkDialog } from "@/Utils/Dialogs";
import AudioOutputs from "@/Case/voip/components/AudioOutputs";

const INITIAL_START_TIME = "00:00";

export default {
  name: "VoIPControls",
  components: {
    "v-icon": Icon,
    AudioOutputs: AudioOutputs,
  },

  props: {
    hospitalName: { default: "", type: String },
  },

  data() {
    return {
      callTime: INITIAL_START_TIME,
      callTimeIntervalID: null,
      connectedAt: null,
      soundIsOn: true,
    };
  },

  computed: {
    ...mapState("Voip", ["callStatus", "muted", "emergencyDepartment", "errorMessage"]),
    showCallTime() {
      return this.callStatus === CallStatus.CONNECTED || this.callStatus === CallStatus.DISCONNECTED;
    },
    departmentName() {
      return this.emergencyDepartment?.name;
    },

    showConnecting() {
      return this.callStatus === CallStatus.CONNECTING;
    },

    showDisconnected() {
      return this.callStatus === CallStatus.DISCONNECTED;
    },
    showRecording() {
      return this.emergencyDepartment?.allowsToRecord === true && this.callStatus === CallStatus.CONNECTED;
    },
  },

  watch: {
    callStatus(value) {
      switch (value) {
        case CallStatus.CONNECTED:
          this.connectedAt = Date.now();
          this.callTimeIntervalID = setInterval(() => {
            this.callTime = minutesAndSecondsElapsed({
              since: this.connectedAt,
            });
          }, 1000);
          break;
        case CallStatus.DISCONNECTED:
          if (this.callTimeIntervalID !== null) {
            clearInterval(this.callTimeIntervalID);
          }
          break;
      }
    },
    errorMessage(newError) {
      if (newError) {
        showOkDialog(this.$bvModal, "Warning", newError, this.resetError);
      }
    },
  },

  beforeMount() {
    Icon.register({
      hang_up: {
        width: 25,
        height: 25,
        d: "M5.57428 15.666C6.31024 14.93 7.14797 14.3225 8.05905 13.864C8.50905 13.6429 8.81655 13.1254 8.81101 12.7103L9.14229 10.3383C11.5465 9.60757 13.4773 9.60726 15.8583 10.3377L16.1844 12.6444C16.1841 13.1586 16.4671 13.6263 16.9299 13.8679C17.8455 14.329 18.6832 14.9358 19.4195 15.6721C19.9514 16.204 20.8168 16.204 21.3487 15.6721L23.5562 13.4646C24.0881 12.9327 24.0881 12.0673 23.5562 11.5354C17.4597 5.43893 7.54024 5.43895 1.44376 11.5354C0.911899 12.0673 0.911901 12.9327 1.44376 13.4646L3.64517 15.666C4.17702 16.1978 5.04246 16.1978 5.57428 15.666Z",
      },
      microphone: {
        width: 19,
        height: 24,
        d: "M14.2917 5.74998C14.2917 4.47915 13.7869 3.26037 12.8882 2.36176C11.9896 1.46315 10.7709 0.958313 9.50003 0.958313C6.85366 0.958313 4.70836 3.10362 4.70836 5.74998V10.5833C4.70836 13.2297 6.85366 15.375 9.50003 15.375C12.1464 15.375 14.2917 13.2297 14.2917 10.5833V5.74998ZM2.85878 11.5416H0.927734C1.37659 15.5461 4.53723 18.7067 8.54169 19.1556V23.0416H10.4584V19.1556C14.4624 18.7063 17.6226 15.5458 18.0714 11.5416H16.1394C15.6594 14.8396 12.8317 17.2855 9.49907 17.2855C6.1664 17.2855 3.33875 14.8396 2.85878 11.5416Z",
      },
      microphone_off: {
        width: 21,
        height: 23,
        d: "M6.36479 3.32641L14.779 11.7406H14.7809C15.1184 11.0716 15.2937 10.3326 15.2926 9.58336L15.2917 5.75003C15.293 3.58612 13.8438 1.68988 11.7555 1.12291C9.66717 0.555936 7.45801 1.45895 6.36479 3.32641ZM14.7406 17.0957C13.7297 17.6679 12.613 18.0285 11.4583 18.1557V22.0417H9.54167V18.1557C5.53721 17.7068 2.37657 14.5462 1.92771 10.5417H3.85875C4.1617 12.6392 5.43769 14.4703 7.30053 15.4808C9.16337 16.4913 11.3941 16.5624 13.3175 15.6726L11.8311 14.1872C10.384 14.6057 8.82433 14.3193 7.62031 13.4139C6.41628 12.5086 5.70809 11.0898 5.70834 9.58336V8.06345L0.334961 2.69103L1.691 1.33499L20.665 20.31L19.309 21.6651L14.7406 17.0957ZM17.5677 14.5284L16.1848 13.1464C16.6726 12.3702 17.0052 11.4885 17.1403 10.5417H19.0723C18.9141 11.976 18.3966 13.3472 17.5677 14.5284Z",
      },
      sound: {
        width: 22,
        height: 18,
        d: "M4.889 13H1C0.447715 13 0 12.5523 0 12V6.00003C0 5.44774 0.447715 5.00003 1 5.00003H4.889L10.183 0.668027C10.3325 0.545446 10.5393 0.520056 10.714 0.602824C10.8888 0.685592 11.0001 0.861683 11 1.05503V16.945C11.0001 17.1384 10.8888 17.3145 10.714 17.3972C10.5393 17.48 10.3325 17.4546 10.183 17.332L4.89 13H4.889ZM18.406 17.134L16.99 15.718C18.9075 14.0119 20.0031 11.5667 20 9.00003C20.0025 6.29869 18.7891 3.73968 16.696 2.03203L18.116 0.612027C20.5823 2.69997 22.0032 5.76861 22 9.00003C22 12.223 20.614 15.122 18.406 17.134ZM13.441 12.169L14.863 13.591C16.17 12.49 17 10.842 17 9.00003C17.0016 7.09297 16.0952 5.29907 14.559 4.16903L13.12 5.60803C14.25 6.31503 15 7.57003 15 9.00003C15.0011 10.2413 14.425 11.4124 13.441 12.169Z",
      },
      sound_off: {
        width: 23,
        height: 18,
        d: "M1 13H4.889H4.89L10.183 17.332C10.3325 17.4546 10.5393 17.48 10.714 17.3972C10.8888 17.3145 11.0001 17.1384 11 16.945V1.05503C11.0001 0.861683 10.8888 0.685592 10.714 0.602824C10.5393 0.520056 10.3325 0.545446 10.183 0.668027L4.889 5.00003H1C0.447715 5.00003 0 5.44774 0 6.00003V12C0 12.5523 0.447715 13 1 13ZM22.95 12.536L19.414 9.00003L22.95 5.46403L21.536 4.05003L18 7.58603L14.464 4.05003L13.05 5.46403L16.586 9.00003L13.05 12.536L14.464 13.95L18 10.414L21.536 13.95L22.95 12.536Z",
      },
    });
  },

  methods: {
    ...mapActions("Voip", {
      startVoipCall: "startCall",
      endVoipCall: "endCall",
      resetError: "resetError",
      toggleMicrophone: "toggleMicrophone",
      getAudioSources: "getAudioSources",
      fillAudioOutputs: "fillAudioOutputs",
    }),
    startCall(emergencyDepartment) {
      this.callTime = INITIAL_START_TIME;
      this.startVoipCall(emergencyDepartment);
    },
    endCall() {
      this.endVoipCall();
    },
    chooseSoundOutput() {
      this.fillAudioOutputs();
      this.$refs.AudioOutputs.show();
    },
  },
};
</script>

<style scoped>
.voip {
  width: 100%;
  background: #ffffff;
  border-radius: 5px;
  bottom: 0;
  color: black;
  padding: 1rem;
  text-align: left;
  z-index: 1;
}

.voip__hospital-name {
  font-weight: bold;
}

.voip__department-name {
  padding-bottom: 0.5rem;
}

.voip-control__button {
  border-radius: 100%;
  width: 3rem;
  height: 3rem;
}

.voip-controls__hangup_button {
  background: #cc0101;
  border: none;
  padding-top: 0.4rem;
}

.voip-controls__hangup_button:hover,
.voip-controls__hangup_button:active {
  background: #e20000;
}

.voip-control__microphone-enabled {
  border: 1px solid #ececec;
  background: transparent;
  padding-top: 0.4rem;
}

.voip-control__sound-enabled {
  border: 1px solid #ececec;
  background: transparent;
  padding-top: 0.25rem;
}

.voip-control__microphone-enabled:hover,
.voip-control__microphone-enabled:active,
.voip-control__sound-enabled:hover,
.voip-control__sound-enabled:active {
  background: #eeeeee;
}

.voip-control__microphone-disabled {
  background: #ececec;
  border: none;
  padding-top: 0.4rem;
}

.voip-control__sound-disabled {
  background: #ececec;
  border: none;
  padding-top: 0.25rem;
}

.voip-control__microphone-disabled:hover,
.voip-control__microphone-disabled:active {
  background: #d6d4d4;
}

.voip-control__call-state {
  color: #555;
  font-size: 0.8rem;
  margin-right: 1rem;
  margin-left: 0.25rem;
}

.voip-control__call-state__block {
  display: inline-block;
}

.voip-controls__recording {
  color: #ce0000;
  display: inline-block;
  font-weight: 700;
  font-size: 1rem;
}

.voip-control__recording__pulsate-circle {
  animation: pulse 2s infinite;
  background-color: #cc0101;
  border-radius: 50%;
  margin-right: 1rem;
  margin-left: 0.5rem;
  height: 10px;
  width: 10px;
}

@keyframes pulse {
  0% {
    transform: scale(0.9);
    box-shadow: 0 0 0 0 rgba(204, 1, 1, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 5px rgba(204, 1, 1, 0);
  }

  100% {
    transform: scale(0.9);
  }
}

.voip-controls__call-time {
  font-size: 1rem;
}

.disconnected {
  margin-right: 1rem;
}
</style>
